/**
 * Folding the lines of a diff that did not change (DOM-free). A run of unchanged lines longer than the lines kept on
 * each side folds into one row, "Show 120 unchanged lines"; a run at the top of a file keeps only its tail (the lines
 * before the first change), one at the bottom only its head. A line the reader has a note on is never folded away.
 */

export interface FoldRow {
  kind: 'add' | 'del' | 'context' | 'hunk' | 'meta';
}

export interface Fold {
  /** First and last row hidden (inclusive). */
  from: number;
  to: number;
  count: number;
}

export interface FoldOptions {
  /** Unchanged lines kept beside a change. */
  keep?: number;
  /** A run folds only when at least this many lines would be hidden. */
  min?: number;
  /** Rows that must stay visible (they carry a note). */
  pinned?: ReadonlySet<number>;
}

export const FOLD_KEEP = 3;
export const FOLD_MIN = 6;

export function foldRuns(rows: readonly FoldRow[], opts: FoldOptions = {}): Fold[] {
  const keep = opts.keep ?? FOLD_KEEP;
  const min = opts.min ?? FOLD_MIN;
  const pinned = opts.pinned;
  const folds: Fold[] = [];
  let i = 0;
  while (i < rows.length) {
    if (rows[i].kind !== 'context' || pinned?.has(i)) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < rows.length && rows[j + 1].kind === 'context' && !pinned?.has(j + 1)) j++;
    // The run is rows i..j. What is on either side says how much of it is context for a change.
    const before = i === 0 ? undefined : rows[i - 1].kind;
    const after = j === rows.length - 1 ? undefined : rows[j + 1].kind;
    const atTop = before === undefined || before === 'hunk' || before === 'meta';
    const atBottom = after === undefined || after === 'hunk' || after === 'meta';
    const head = atTop ? 0 : keep;
    const tail = atBottom ? 0 : keep;
    const from = i + head;
    const to = j - tail;
    if (to - from + 1 >= min) folds.push({ from, to, count: to - from + 1 });
    i = j + 1;
  }
  return folds;
}

/** "Show 120 unchanged lines". */
export function foldLabel(count: number): string {
  return `Show ${count.toLocaleString()} unchanged ${count === 1 ? 'line' : 'lines'}`;
}
